import { KgInsightSentiment } from '@api/api/kg-insights';
import { formatRoundedLegendValue } from '@design/charts/pie-chart/utils';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { usePulseSentimentBreakdown } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSentimentBreakdown';
import { usePulseMonthlySummaries } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { PulseSentimentDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { stripDailySummaryCitations } from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/linkDailySummaryCitations';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './MonthlySentiment.module.scss';

export interface MonthlySentimentProps {
  themeId: string;
}

const sentimentCards: Exclude<PulseSentimentDirection, 'all'>[] = [
  'tailwind',
  'headwind',
];

const sentimentLegend: Array<{
  key: KgInsightSentiment;
  labelKey: 'tailwind' | 'headwind' | 'neutral';
}> = [
  { key: 'positive', labelKey: 'tailwind' },
  { key: 'negative', labelKey: 'headwind' },
  { key: 'neutral', labelKey: 'neutral' },
];

export const MonthlySentiment = ({ themeId }: MonthlySentimentProps) => {
  const { t } = useTranslation(['widget']);
  const monthlySummaries = usePulseMonthlySummaries({ id: themeId });
  const sentimentBreakdown = usePulseSentimentBreakdown({ id: themeId });

  const summaries = monthlySummaries.data;
  const totals = sentimentBreakdown.data?.totals;
  const totalMentions = totals
    ? sentimentLegend.reduce((sum, { key }) => sum + totals[key], 0)
    : 0;

  if (!monthlySummaries.isLoading && summaries === undefined) {
    return null;
  }

  return (
    <section
      className={styles.root}
      data-testid="industry-drawer-monthly-sentiment"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('widget:themeOverview.pulse.drawer.title_all')}
        </h3>
        <div className={styles.legend}>
          {totals && !sentimentBreakdown.isLoading ? (
            sentimentLegend.map(({ key, labelKey }) => {
              if (totals[key] === 0) {
                return null;
              }

              const sentiment = t(`widget:themeOverview.pulse.${labelKey}`);
              const percent = formatRoundedLegendValue(
                totals[key] / totalMentions
              );

              return (
                <span key={key} className={styles.legendItem}>
                  <span
                    className={clsx(styles.legendDot, styles[key])}
                    aria-hidden
                  />
                  {t('widget:themeOverview.pulse.sentimentPercent', {
                    sentiment,
                    percent,
                  })}
                </span>
              );
            })
          ) : (
            <SkeletonLoader
              className={styles.legendSkeleton}
              areas={sentimentLegend.map(() => ({
                className: styles.legendSkeletonItem,
              }))}
            />
          )}
        </div>
      </div>
      {monthlySummaries.isLoading ? (
        <SkeletonLoader
          areas={[
            {
              styles: { width: '100%', height: '200px', borderRadius: '8px' },
            },
          ]}
        />
      ) : (
        <ErrorBoundaryWrapper showBorder={false}>
          <div className={styles.cards}>
            {summaries &&
              sentimentCards.map(direction => {
                const narrative = summaries[direction].narrative;
                const isEmptyNarrative = narrative.trim() === '';

                return (
                  <article
                    key={direction}
                    className={clsx(styles.card, styles[direction])}
                    data-testid={`industry-drawer-sentiment-card-${direction}`}
                  >
                    <span className={styles.cardTitle}>
                      {t(`widget:themeOverview.pulse.${direction}Sentiment`)}
                    </span>
                    <span
                      className={clsx(styles.narrative, {
                        [styles.emptyNarrative]: isEmptyNarrative,
                      })}
                    >
                      {isEmptyNarrative ? (
                        t(
                          `widget:themeOverview.pulse.noSignalsInLast30Days_${direction}`
                        )
                      ) : (
                        <Markdown
                          message={stripDailySummaryCitations(narrative)}
                        />
                      )}
                    </span>
                  </article>
                );
              })}
          </div>
        </ErrorBoundaryWrapper>
      )}
    </section>
  );
};
